import {
  Box,
  Flex,
  Grid,
  Icon,
  Input,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  HiOutlineArrowLeft,
  HiOutlineArrowUpTray,
  HiOutlineClipboardDocument,
  HiOutlineGlobeAlt,
  HiOutlineLink,
  HiOutlineSparkles,
  HiOutlineVideoCamera,
  HiSparkles,
} from "react-icons/hi2";

import { BhLogo } from "../../../../components";
import { useExtractTextFromUrl } from "../FlexIntake/useExtractTextFromUrl";
import { AgenticChatMessage } from "./AgenticChatMessage";
import { DocumentUploadCard } from "./DocumentUploadCard";
import { DuplicateJobDescriptionModal } from "./DuplicateJobDescriptionModal";
import { SelectRecordingModal } from "./SelectRecordingModal";
import { createFileFromText } from "./textToFile";
import type { DocumentState } from "./types";
import { useAgenticChat } from "./useAgenticChat";
import type { DocumentUploadState } from "./useDocumentUpload";

const TYPING_KEYFRAMES = {
  "@keyframes typingBounce": {
    "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.4 },
    "30%": { transform: "translateY(-4px)", opacity: 1 },
  },
};

const BH_GRADIENT = "linear(to-r, #0074DD, #581EFF, #9643FF)";
const BH_GRADIENT_SVG_ID = "bh-chat-logo-gradient";

type InputMode =
  | "upload"
  | "url"
  | "ats"
  | "plan"
  | "paste"
  | "recording"
  | null;

type ActionButtonDef = {
  label: string;
  icon: React.ElementType;
  mode: NonNullable<InputMode>;
  hidden?: boolean;
};

const ACTION_BUTTONS: ActionButtonDef[] = [
  {
    label: "Upload document or file",
    icon: HiOutlineArrowUpTray,
    mode: "upload",
    hidden: false,
  },
  {
    label: "Link to public URL",
    icon: HiOutlineGlobeAlt,
    mode: "url",
    hidden: true,
  },
  {
    label: "Import from ATS",
    icon: HiOutlineLink,
    mode: "ats",
    hidden: true,
  },
  {
    label: "Select from BrightHire Plan",
    icon: HiOutlineSparkles,
    mode: "plan",
    hidden: true,
  },
  {
    label: "Type or paste text",
    icon: HiOutlineClipboardDocument,
    mode: "paste",
    hidden: false,
  },
  {
    label: "Add BrightHire recording",
    icon: HiOutlineVideoCamera,
    mode: "recording",
    hidden: false,
  },
];

type AgenticChatViewProps = {
  displayName: string;
  aiInterviewerId?: string | null;
  autoCreateGuide?: boolean;
  onGenerate: () => void;
  onSkipGenerate?: () => void;
  onJobDescriptionProvided?: (text: string) => void;
  onDocumentProvided?: (
    documentType: keyof import("./types").DocumentState,
    text: string
  ) => void;
  onStateChange?: (state: {
    documentState: DocumentState;
    jobDescriptionProvided: boolean;
    uploads: DocumentUploadState[];
    removeUpload: (uploadId: string) => void;
    sendFileMessage: (file: File) => void;
  }) => void;
};

export const AgenticChatView: React.FC<AgenticChatViewProps> = ({
  displayName,
  aiInterviewerId = null,
  autoCreateGuide = true,
  onGenerate,
  onSkipGenerate,
  onJobDescriptionProvided = () => undefined,
  onDocumentProvided = () => undefined,
  onStateChange,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [activeMode, setActiveMode] = useState<InputMode>(null);
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const pasteInputRef = useRef<HTMLTextAreaElement>(null);
  const [urlValue, setUrlValue] = useState("");
  const [pasteValue, setPasteValue] = useState("");
  const didPasteRef = useRef(false);

  const {
    messages,
    documentState,
    jobDescriptionProvided,
    isStreaming,
    isInitializing,
    isTyping,
    error,
    uploads,
    pendingDuplicateJd,
    sendFileMessage,
    sendRecordingMessage,
    removeUpload,
    resolveDuplicateJd,
  } = useAgenticChat({
    displayName,
    aiInterviewerId,
  });

  const {
    extract: extractUrl,
    loading: urlExtractLoading,
    error: urlExtractError,
    result: urlExtractResult,
    reset: resetUrlExtract,
  } = useExtractTextFromUrl();

  useEffect(() => {
    if (!urlExtractResult?.extractedText) return;
    const file = createFileFromText(
      urlExtractResult.extractedText,
      "url",
      urlExtractResult.sourceUrl
    );
    sendFileMessage(file);
    setUrlValue("");
    setActiveMode(null);
    resetUrlExtract();
  }, [urlExtractResult, sendFileMessage, resetUrlExtract]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming, isTyping, uploads.length]);

  useEffect(() => {
    onStateChange?.({
      documentState,
      jobDescriptionProvided,
      uploads,
      removeUpload,
      sendFileMessage,
    });
  }, [
    documentState,
    jobDescriptionProvided,
    uploads,
    removeUpload,
    sendFileMessage,
    onStateChange,
  ]);

  useEffect(() => {
    if (activeMode === "url") urlInputRef.current?.focus();
    else if (activeMode === "paste") pasteInputRef.current?.focus();
  }, [activeMode]);

  const disableInput = isStreaming || isInitializing || isTyping;

  const processingCount = uploads.filter(
    (u) => u.phase !== "complete" && u.phase !== "error"
  ).length;
  const isProcessing = processingCount > 0;
  const canGenerate = jobDescriptionProvided && !isProcessing;
  const generateTooltip = isProcessing
    ? `${processingCount} document${
        processingCount > 1 ? "s" : ""
      } still processing — please wait`
    : "Job Description required";

  const visibleActions = ACTION_BUTTONS.filter((a) => !a.hidden);

  const actionRows = Math.ceil(visibleActions.length / 2);
  const actionBarContentHeight = actionRows * 32 + (actionRows - 1) * 8;

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList?.length) return;
      for (let i = 0; i < fileList.length; i++) {
        sendFileMessage(fileList[i]);
      }
      setActiveMode(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [sendFileMessage]
  );

  const handleUrlSubmit = useCallback(() => {
    const trimmed = urlValue.trim();
    if (!trimmed) return;
    resetUrlExtract();
    extractUrl(trimmed);
  }, [urlValue, extractUrl, resetUrlExtract]);

  const handlePasteSubmit = useCallback(() => {
    const trimmed = pasteValue.trim();
    if (!trimmed) return;
    const source = didPasteRef.current ? "pasted" : "typed";
    const file = createFileFromText(trimmed, source);
    sendFileMessage(file);
    setPasteValue("");
    didPasteRef.current = false;
    setActiveMode(null);
  }, [pasteValue, sendFileMessage]);

  const handleBackToModes = useCallback(() => {
    setActiveMode(null);
    setUrlValue("");
    setPasteValue("");
    didPasteRef.current = false;
    resetUrlExtract();
  }, [resetUrlExtract]);

  const handleRecordingSelect = useCallback(
    (callId: string, callName: string, positionTitle?: string | null) => {
      setIsRecordingModalOpen(false);
      sendRecordingMessage(callId, callName, positionTitle);
    },
    [sendRecordingMessage]
  );

  const renderActiveControl = () => {
    switch (activeMode) {
      case "upload":
        return (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <Flex
              as="button"
              direction="column"
              align="center"
              justify="center"
              p="4"
              w="100%"
              h="100%"
              border="2px dashed"
              borderColor="gray.200"
              borderRadius="lg"
              color="gray.900"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ bg: "gray.50" }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon as={HiOutlineArrowUpTray} boxSize="5" />
              <Text fontSize="xs" fontWeight="medium">
                Click to choose a file
              </Text>
              <Text fontSize="2xs" opacity={0.7}>
                or drag &amp; drop anywhere
              </Text>
            </Flex>
          </>
        );

      case "url":
        return (
          <VStack spacing="2" w="100%" align="stretch">
            <Flex gap="2" w="100%" align="center">
              <Input
                ref={urlInputRef}
                flex="1"
                placeholder="https://example.com/job-description"
                value={urlValue}
                onChange={(e) => {
                  setUrlValue(e.target.value);
                  if (urlExtractError) resetUrlExtract();
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && !urlExtractLoading && handleUrlSubmit()
                }
                size="sm"
                border="1px solid"
                borderColor={urlExtractError ? "red.300" : "gray.200"}
                color="gray.900"
                borderRadius="md"
                _placeholder={{ color: "gray.400" }}
                _focus={{ borderColor: "blue.400", bg: "gray.50" }}
                isDisabled={urlExtractLoading}
              />
              {urlExtractLoading && <Spinner size="sm" color="gray.500" />}
            </Flex>
            {urlExtractError && (
              <Text fontSize="2xs" color="red.500">
                {urlExtractError}
              </Text>
            )}
          </VStack>
        );

      case "ats":
        return (
          <Flex
            align="center"
            justify="center"
            w="100%"
            h="100%"
            color="gray.500"
            fontSize="sm"
          >
            <Text>ATS import coming soon</Text>
          </Flex>
        );

      case "plan":
        return (
          <Flex
            align="center"
            justify="center"
            w="100%"
            h="100%"
            color="gray.500"
            fontSize="sm"
          >
            <Text>BrightHire Plan import coming soon</Text>
          </Flex>
        );

      case "paste":
        return (
          <Textarea
            ref={pasteInputRef}
            placeholder="Paste your job description, interview notes, or other materials here..."
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
            onPaste={() => {
              didPasteRef.current = true;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                handlePasteSubmit();
            }}
            size="sm"
            h="100%"
            minH="0"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            color="gray.800"
            borderRadius="md"
            _placeholder={{ color: "gray.400" }}
            _focus={{ borderColor: "blue.400", bg: "white" }}
            resize="none"
          />
        );

      default:
        return null;
    }
  };

  return (
    <Flex
      direction="column"
      h="100%"
      minH="0"
      w="100%"
      position="relative"
      bg="white"
      overflow="hidden"
    >
      <DuplicateJobDescriptionModal
        pending={pendingDuplicateJd}
        onKeepExisting={() => resolveDuplicateJd("keep-existing")}
        onUseNew={() => resolveDuplicateJd("replace")}
      />
      <SelectRecordingModal
        isOpen={isRecordingModalOpen}
        onClose={() => setIsRecordingModalOpen(false)}
        onSelect={handleRecordingSelect}
      />

      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        px="6"
        py="4"
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="white"
      >
        <Flex align="center" gap="2">
          <svg width="0" height="0" style={{ position: "absolute" }}>
            <defs>
              <linearGradient
                id={BH_GRADIENT_SVG_ID}
                x1="0"
                y1="0"
                x2="1"
                y2="1"
              >
                <stop offset="0%" stopColor="#0074DD" />
                <stop offset="48%" stopColor="#591EFF" />
                <stop offset="100%" stopColor="#9644FF" />
              </linearGradient>
            </defs>
          </svg>
          <BhLogo
            boxSize="6"
            sx={{ "& path": { fill: `url(#${BH_GRADIENT_SVG_ID})` } }}
          />
          <Text
            fontSize="lg"
            fontWeight="semibold"
            bgGradient={BH_GRADIENT}
            bgClip="text"
          >
            BrightHire AI
          </Text>
        </Flex>
      </Flex>

      {/* Messages */}
      <Flex direction="column" flex="1" overflowY="auto" px="6" bg="white">
        <Box flex="1" />
        <VStack align="stretch" spacing="5" py="5">
          {messages.map((msg) => {
            if (msg.uploadId) {
              const upload = uploads.find((u) => u.id === msg.uploadId);
              if (!upload) return null;
              return (
                <Flex key={msg.id} justify="flex-end">
                  <Box maxW="80%">
                    <DocumentUploadCard
                      phase={upload.phase}
                      fileName={upload.fileName}
                      fileMimeType={upload.fileMimeType}
                      fileSizeBytes={upload.fileSizeBytes}
                      uploadProgress={upload.uploadProgress}
                      analysis={upload.analysis}
                      errorMessage={upload.errorMessage}
                      onRemove={() => removeUpload(upload.id)}
                    />
                  </Box>
                </Flex>
              );
            }
            return <AgenticChatMessage key={msg.id} message={msg} />;
          })}
          {isTyping && (
            <Flex align="center" gap="1.5" py="2" pl="1">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg="purple.400"
                  sx={{
                    ...TYPING_KEYFRAMES,
                    animation: "typingBounce 1.4s ease-in-out infinite",
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </Flex>
          )}
          {isStreaming && (
            <Flex align="center" gap="2" py="1">
              <Spinner size="xs" color="purple.500" />
              <Text fontSize="sm" color="gray.400">
                Thinking...
              </Text>
            </Flex>
          )}
          {isInitializing && (
            <Flex align="center" gap="2" py="1">
              <Spinner size="xs" color="blue.400" />
              <Text fontSize="sm" color="gray.400">
                Setting up session...
              </Text>
            </Flex>
          )}
          {error && (
            <Box
              px="4"
              py="2"
              borderRadius="md"
              bg="red.50"
              borderColor="red.200"
              borderWidth="1px"
            >
              <Text fontSize="sm" color="red.700">
                {error}
              </Text>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </VStack>
      </Flex>

      {/* Action bar */}
      <Box
        px="3"
        pt="2.5"
        pb="2.5"
        borderTop="1px solid"
        borderColor="gray.200"
      >
        <Box
          h={activeMode ? "auto" : `${actionBarContentHeight}px`}
          minH={`${actionBarContentHeight}px`}
        >
          {activeMode === null ? (
            <Grid templateColumns="1fr 1fr" gap="2" h="100%">
              {visibleActions.map((action) => (
                <Flex
                  key={action.label}
                  as="button"
                  position="relative"
                  align="center"
                  justify="center"
                  px="3"
                  h="8"
                  fontWeight="medium"
                  fontSize="sm"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  cursor={disableInput ? "not-allowed" : "pointer"}
                  opacity={disableInput ? 0.5 : 1}
                  transition="all 0.15s"
                  _hover={!disableInput ? { bg: "gray.50" } : {}}
                  onClick={() => {
                    if (disableInput) return;
                    if (action.mode === "recording") {
                      setIsRecordingModalOpen(true);
                    } else {
                      setActiveMode(action.mode);
                    }
                  }}
                >
                  <Icon
                    as={action.icon}
                    width="18px"
                    height="18px"
                    position="absolute"
                    left="3"
                    flexShrink={0}
                  />
                  {action.label}
                </Flex>
              ))}
            </Grid>
          ) : (
            <Flex direction="column" h="100%" w="100%">
              <Box flex="1" w="100%" minH="0">
                {renderActiveControl()}
              </Box>
              <Flex
                w="100%"
                justify="space-between"
                align="center"
                h="7"
                flexShrink={0}
                mt="1.5"
              >
                <Flex
                  as="button"
                  align="center"
                  gap="1.5"
                  color="gray.900"
                  fontSize="xs"
                  fontWeight="medium"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ bg: "gray.50" }}
                  onClick={handleBackToModes}
                >
                  <Icon as={HiOutlineArrowLeft} boxSize="3.5" />
                  Add in a different way
                </Flex>
                {(activeMode === "url" || activeMode === "paste") && (
                  <Flex
                    as="button"
                    align="center"
                    justify="center"
                    px="4"
                    h="7"
                    bg="gray.100"
                    color="gray.900"
                    fontSize="xs"
                    fontWeight="medium"
                    borderRadius="md"
                    cursor={
                      (
                        activeMode === "url"
                          ? urlValue.trim() && !urlExtractLoading
                          : pasteValue.trim()
                      )
                        ? "pointer"
                        : "not-allowed"
                    }
                    opacity={
                      (
                        activeMode === "url"
                          ? urlValue.trim() && !urlExtractLoading
                          : pasteValue.trim()
                      )
                        ? 1
                        : 0.5
                    }
                    _hover={
                      (
                        activeMode === "url"
                          ? urlValue.trim() && !urlExtractLoading
                          : pasteValue.trim()
                      )
                        ? { bg: "gray.200" }
                        : {}
                    }
                    onClick={
                      activeMode === "url" ? handleUrlSubmit : handlePasteSubmit
                    }
                  >
                    {activeMode === "url" && urlExtractLoading
                      ? "Extracting..."
                      : "Submit"}
                  </Flex>
                )}
              </Flex>
            </Flex>
          )}
        </Box>

        {autoCreateGuide ? (
          <Tooltip
            label={generateTooltip}
            isDisabled={canGenerate}
            hasArrow
            placement="top"
          >
            <Flex
              as="button"
              type="button"
              align="center"
              justify="center"
              gap="2"
              w="100%"
              mt="2"
              h="10"
              fontSize="md"
              fontWeight="medium"
              color="white"
              bg="accent.600"
              borderRadius="lg"
              cursor={canGenerate ? "pointer" : "not-allowed"}
              opacity={canGenerate ? 1 : 0.4}
              _hover={canGenerate ? { opacity: 0.9 } : {}}
              _active={canGenerate ? { opacity: 0.8 } : {}}
              onClick={canGenerate ? onGenerate : undefined}
            >
              <Icon as={HiSparkles} boxSize="5" />
              Generate interview guide
            </Flex>
          </Tooltip>
        ) : (
          <Flex
            as="button"
            type="button"
            align="center"
            justify="center"
            w="100%"
            mt="2"
            h="10"
            fontSize="md"
            fontWeight="medium"
            color="white"
            bg="accent.600"
            borderRadius="lg"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            _active={{ opacity: 0.8 }}
            onClick={onSkipGenerate}
          >
            Next: Interview guide
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
